/**
 * Categories and fix kinds per agent template (plan-032 DOM-OR, design §43.5.3, FR-ORE-7).
 *
 * One rubric per family of agent templates. A category outside its template's closed set is dropped on receipt (the
 * observe pass) and refused in a finding; a fix kind the template may not propose is refused by `optimisationFixOf`.
 * The order of each category list is the rubric's own order, which breaks ties in a finding's `position`
 * (design §43.6.3).
 *
 * `help` is never reviewed (`not_offered`): the help assistant is wetalk's, not the account's.
 */
import { HELP_AGENT_TEMPLATE_CODE } from "../../template/agent-template-catalogue.js";
/** The fix kinds of design §43.6.1, as `optimisation_finding.fix_kind` stores them. */
export const OPTIMISATION_FIX_KIND = ["answer", "config_patch", "advice"];
/** The rubric families: one prompt file each under `prompts/optimisation-review/`. */
export const OPTIMISATION_REVIEW_RUBRIC = ["support", "delivery", "survey", "receptionist", "bi"];
/** Design §43.5.3, verbatim: the closed category set of each rubric, in the rubric's order. */
export const OPTIMISATION_FINDING_CATEGORY = Object.freeze({
    support: Object.freeze([
        "unanswered_question",
        "wrong_or_vague_answer",
        "information_missing",
        "unneeded_handoff",
        "message_taking_friction",
        "opening_hours",
        "greeting",
    ]),
    delivery: Object.freeze([
        "item_not_understood",
        "price_or_availability_question",
        "order_abandoned",
        "address_or_phone_friction",
        "opening_hours",
        "greeting",
        "unanswered_question",
    ]),
    survey: Object.freeze([
        "opening_declined",
        "question_needs_clarification",
        "drop_off_at_question",
        "interviewer_line",
        "respondent_question_unanswered",
    ]),
    receptionist: Object.freeze([
        "unanswered_question",
        "routing_confusion",
        "message_taking_friction",
        "opening_hours",
        "greeting",
    ]),
    bi: Object.freeze(["data_question_unanswered", "wrong_or_vague_answer", "greeting"]),
});
/** Design §43.5.3: the fix kinds each rubric may propose. A survey proposes advice only (the Questions tab owns it). */
export const OPTIMISATION_FIX_KIND_BY_RUBRIC = Object.freeze({
    support: Object.freeze(["answer", "config_patch", "advice"]),
    delivery: Object.freeze(["answer", "config_patch", "advice"]),
    survey: Object.freeze(["advice"]),
    receptionist: Object.freeze(["answer", "config_patch", "advice"]),
    bi: Object.freeze(["answer", "advice"]),
});
/**
 * The rubric an agent template is reviewed under, or `null` when it is not reviewed (`help`, or a code this release
 * does not know). `booking` and `leads` read as a receptionist: a call answered, routed or taken as a message.
 */
export function optimisationReviewRubricOf(agentTemplateCode) {
    switch (agentTemplateCode) {
        case "support":
        case "delivery":
        case "survey":
        case "receptionist":
        case "bi":
            return agentTemplateCode;
        case "booking":
        case "leads":
            return "receptionist";
        case HELP_AGENT_TEMPLATE_CODE:
            return null;
        default:
            return null;
    }
}
/** Whether `category` belongs to `rubric`'s closed set. */
export function isOptimisationFindingCategoryOf(rubric, category) {
    return OPTIMISATION_FINDING_CATEGORY[rubric].includes(category);
}
/** The category's place in its rubric's order (design §43.6.3's tie-break); `-1` when it is not in the set. */
export function optimisationFindingCategoryRankOf(rubric, category) {
    return OPTIMISATION_FINDING_CATEGORY[rubric].indexOf(category);
}
/** Whether `rubric` may propose a fix of `fixKind`. */
export function isOptimisationFixKindOfferedBy(rubric, fixKind) {
    return OPTIMISATION_FIX_KIND_BY_RUBRIC[rubric].includes(fixKind);
}
//# sourceMappingURL=category.js.map